import { Expression } from '../../query-engine/interfaces/query.interface';

/**
 * Severity levels, matching the backend `condition_severity` enum verbatim so no mapping layer is
 * needed. Order matters: when several conditions match the same row the most severe one wins.
 */
export type ConditionSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export const CONDITION_SEVERITIES: ConditionSeverity[] = ['CRITICAL', 'WARNING', 'INFO'];

/** Rank used to pick a winner when a row satisfies more than one condition (higher wins). */
export const SEVERITY_RANK: Record<ConditionSeverity, number> = {
  CRITICAL: 3,
  WARNING: 2,
  INFO: 1,
};

/**
 * Default highlight colour per severity. Resolved from the CSS custom properties defined in the
 * theme so the palette stays themeable; these hex values are only the fallback used when the
 * property is not set (e.g. inside a unit test or an unstyled host).
 */
export const SEVERITY_FALLBACK_COLOR: Record<ConditionSeverity, string> = {
  CRITICAL: '#FF4F79',
  WARNING: '#FFBE0A',
  INFO: '#4169E1',
};

export const SEVERITY_CSS_VAR: Record<ConditionSeverity, string> = {
  CRITICAL: '--wbi-condition-critical',
  WARNING: '--wbi-condition-warning',
  INFO: '--wbi-condition-info',
};

/** A condition as returned by `GET /v1/conditions`. */
export interface ICondition {
  id: string;
  name: string;
  description?: string | null;
  /** Boolean expression tree evaluated by the query engine. */
  expression: Expression;
  /** Query the backend evaluator runs for this condition; unused by the chart highlight path. */
  query_id?: string | null;
  severity: ConditionSeverity;
  /** Overrides the severity's default colour. */
  color?: string | null;
  /** Message shown when met; supports `{{column}}` placeholders. */
  text_template?: string | null;
  status: 'ACTIVE' | 'DISABLED';
  /** Created by the assistant (WB-222); a row without the mark is manual. */
  ai_generated?: boolean;
  created_at?: string | Date;
  updated_at?: string | Date;
}

export interface ICreateConditionDTO {
  name: string;
  description?: string;
  expression: Expression;
  query_id?: string;
  severity?: ConditionSeverity;
  color?: string;
  text_template?: string;
  status?: 'ACTIVE' | 'DISABLED';
}
